// harmony-search.js — instant product search inside [data-h-search].
// The default state is server-rendered (featured collection); typing
// debounces (~300ms) into /search/suggest.json and re-renders
// [data-h-search-results] with the same card markup; clearing restores the
// cached default HTML. In-flight requests abort on new keystrokes.
// Localized strings + config ride in the [data-h-search-i18n] JSON block.
(function () {
  if (window.__hSearchInit) return;
  window.__hSearchInit = true;

  const DEBOUNCE_MS = 300;
  let controller = null;

  const i18n = (root) => {
    if (!root._hI18n) {
      const el = root.querySelector('[data-h-search-i18n]');
      root._hI18n = el ? JSON.parse(el.textContent) : {};
    }
    return root._hI18n;
  };

  const tpl = (s, values) =>
    Object.entries(values).reduce((out, [k, v]) => out.replaceAll(k, v), s || '');

  const money = (t, amount) =>
    new Intl.NumberFormat(t.locale || 'fr', { style: 'currency', currency: t.currency || 'EUR' }).format(
      parseFloat(amount)
    );

  /* Mirrors the Liquid product card in snippets/harmony-search.liquid. */
  const card = (t, product) => {
    const a = document.createElement('a');
    a.href = product.url;
    a.className = 'group flex flex-col gap-h-3';
    const compare = parseFloat(product.compare_at_price_min) > parseFloat(product.price_min);
    a.innerHTML = `
      <div class="aspect-[4/5] overflow-hidden rounded-h-default bg-h-white">
        ${
          product.featured_image && product.featured_image.url
            ? `<img src="${product.featured_image.url}&width=480" alt="" loading="lazy" class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]">`
            : ''
        }
      </div>
      <div class="flex flex-col gap-h-1">
        <p class="m-0 line-clamp-2 text-h-sm font-semibold leading-snug text-h-black" data-t></p>
        <p class="m-0 text-h-sm font-semibold text-h-antique-brass">
          <span data-p></span>${compare ? ' <s class="font-normal text-h-black-40" data-cp></s>' : ''}
        </p>
      </div>`;
    const img = a.querySelector('img');
    if (img) img.alt = product.featured_image.alt || product.title;
    a.querySelector('[data-t]').textContent = product.title;
    a.querySelector('[data-p]').textContent = money(t, product.price_min ?? product.price);
    const cp = a.querySelector('[data-cp]');
    if (cp) cp.textContent = money(t, product.compare_at_price_min);
    return a;
  };

  /* Mirrors the Liquid results block: heading row + grid, or a
     no-results message when the query matches nothing. */
  const renderResults = (root, query, products) => {
    const t = i18n(root);
    const wrap = document.createElement('div');
    wrap.className = 'flex flex-col gap-h-6';
    const head = document.createElement('div');
    head.className = 'flex items-baseline justify-between gap-h-4';
    head.innerHTML = `
      <h3 class="m-0 font-harmony text-h-base font-bold uppercase" data-h></h3>
      <a class="shrink-0 text-h-sm text-h-black underline underline-offset-4 transition-opacity hover:opacity-60" data-a></a>`;
    head.querySelector('[data-h]').textContent =
      products.length > 0
        ? tpl(products.length === 1 ? t.countOne : t.countOther, { __COUNT__: products.length })
        : t.productsHeading;
    const link = head.querySelector('[data-a]');
    link.textContent = tpl(t.searchFor, { __TERMS__: query });
    link.href = `${t.searchUrl}?q=${encodeURIComponent(query)}`;
    wrap.appendChild(head);
    if (products.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'm-0 py-h-8 text-center text-h-base text-h-black-60';
      empty.textContent = tpl(t.noResults, { __TERMS__: query });
      wrap.appendChild(empty);
      return wrap;
    }
    const grid = document.createElement('div');
    grid.className = 'grid grid-cols-2 gap-h-4 md:grid-cols-[repeat(var(--h-search-cols,4),minmax(0,1fr))]';
    products.forEach((p) => grid.appendChild(card(t, p)));
    wrap.appendChild(grid);
    return wrap;
  };

  const run = async (root, query) => {
    const results = root.querySelector('[data-h-search-results]');
    if (!results) return;
    if (root._hDefault === undefined) root._hDefault = results.innerHTML;
    if (!query) {
      results.innerHTML = root._hDefault;
      return;
    }
    controller?.abort();
    controller = new AbortController();
    const t = i18n(root);
    try {
      const url = `${t.searchUrl}/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${root.dataset.limit || 4}&resources[options][unavailable_products]=last`;
      const data = await fetch(url, { signal: controller.signal }).then((r) => r.json());
      const products = data?.resources?.results?.products || [];
      results.replaceChildren(renderResults(root, query, products));
    } catch (e) {
      /* Aborted (newer keystroke owns the render) or offline — keep the
         current content rather than blanking the panel. */
    }
  };

  document.addEventListener('input', (e) => {
    const input = e.target.closest?.('[data-h-search-input]');
    if (!input) return;
    const root = input.closest('[data-h-search]');
    if (!root) return;
    clearTimeout(root._hSearchTimer);
    root._hSearchTimer = setTimeout(() => run(root, input.value.trim()), DEBOUNCE_MS);
  });
})();
